import { useState } from "react";
import { Send, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const StepEmail = ({ email, setEmail, onNext, onCancel }) => {
  const ok = EMAIL_RE.test(email.trim());
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="send-email">Email del destinatario</Label>
        <Input id="send-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@azienda.it" autoFocus data-testid="send-quote-email-input" />
        {!ok && <p className="text-xs text-red-400" data-testid="send-quote-email-error">{email ? "Indirizzo email non valido" : "Inserisci l'email del cliente"}</p>}
        <p className="text-xs text-slate-500">Controlla bene l'indirizzo: verrà salvato anche nell'anagrafica del cliente.</p>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} data-testid="send-quote-cancel">Annulla</Button>
        <Button onClick={onNext} disabled={!ok} className="bg-red-600 hover:bg-red-500 text-white" data-testid="send-quote-next">Continua</Button>
      </DialogFooter>
    </>
  );
};

const StepConfirm = ({ email, code, cc, sending, onBack, onSend }) => (
  <>
    <div className="rounded-md border border-red-500/50 bg-red-500/10 p-4 text-sm space-y-1" data-testid="send-quote-confirm-box">
      <p className="flex items-center gap-2 font-semibold text-red-300"><AlertTriangle className="h-4 w-4" />Confermi l'invio al cliente?</p>
      <p>Preventivo <b>{code || "nuovo"}</b> a <b className="font-mono" data-testid="send-quote-confirm-email">{email.trim()}</b></p>
      <p className="text-xs text-slate-400">Il cliente riceverà la mail con il PDF completo allegato.</p>
      {(cc || []).filter((c) => c !== email.trim().toLowerCase()).length > 0 && <p className="text-xs text-slate-400" data-testid="send-quote-confirm-cc">In copia (CC): <span className="font-mono">{cc.filter((c) => c !== email.trim().toLowerCase()).join(", ")}</span></p>}
    </div>
    <DialogFooter>
      <Button variant="ghost" onClick={onBack} disabled={sending} data-testid="send-quote-back">Indietro</Button>
      <Button onClick={onSend} disabled={sending} className="bg-red-600 hover:bg-red-500 text-white" data-testid="send-quote-confirm">
        {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Sì, invia preventivo
      </Button>
    </DialogFooter>
  </>
);

export const SendQuoteDialog = ({ open, onOpenChange, initialEmail, code, cc, onSend }) => {
  const [email, setEmail] = useState(initialEmail || "");
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const close = () => { setStep(1); onOpenChange(false); };
  const send = async () => {
    setSending(true);
    const ok = await onSend(email.trim());
    setSending(false);
    if (ok) close();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="bg-slate-900 border-slate-800 w-[calc(100%-2rem)] rounded-lg" data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle>Invia preventivo {step === 2 ? "— conferma" : ""}</DialogTitle>
          <DialogDescription>{step === 1 ? "Passo 1 di 2: verifica l'email del destinatario" : "Passo 2 di 2: conferma finale"}</DialogDescription>
        </DialogHeader>
        {step === 1
          ? <StepEmail email={email} setEmail={setEmail} onNext={() => setStep(2)} onCancel={close} />
          : <StepConfirm email={email} code={code} cc={cc} sending={sending} onBack={() => setStep(1)} onSend={send} />}
      </DialogContent>
    </Dialog>
  );
};
